import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core';
import { ChangableComponent } from '@app/models/changable.component';
import { AccountService, AuthService, CommentService, ContextService, FilesService } from '@core/services';
import { Store } from '@ngrx/store';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { getMe } from '@store/reducers/profile.reducer';
import { Comment, Content, ImageSizeEnum, User, UserStatusEnum } from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiCover } from 'onclass-ui';
import { first, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-comment',
  templateUrl: './comment.component.html',
  styleUrls: ['./comment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentComponent extends ChangableComponent implements OnChanges, OnDestroy {
  @Input() public content: Content;
  @Input() public comment: Comment;
  @Input() public reply_to: Comment;
  @Output() public commented = new EventEmitter<boolean>();
  @Output() public removed = new EventEmitter<boolean>();

  public me: User;
  public isGuest: boolean;
  public publishedAtDate: Date;
  public fullName: string;
  public avatar: OnUiCover;
  public complainForm = false;
  public commentForm = false;
  public replyEnabled = false;
  public likeEnabled = true;
  public accountRoute: string;

  public constructor(
    public errorNotificationService: ErrorNotificationService,
    private authService: AuthService,
    private commentService: CommentService,
    private contextService: ContextService,
    private accountService: AccountService,
    private store: Store,
    protected readonly cdr: ChangeDetectorRef,
    public deviceService: DeviceDetectorService,
  ) {
    super(cdr);
  }

  public ngOnChanges() {
    this.publishedAtDate = new Date(this.comment.published_at);
    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => {
          this.me = me;
          this.isGuest = me && me.status === UserStatusEnum.guest;
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
    if (this.comment.author) {
      this.getAuthorDetails(this.comment.author);
    } else {
      this.loadAuthorDetails(this.comment.author_id);
    }
  }

  private getAuthorDetails(user: User) {
    if (!user) {
      return;
    }

    this.comment.author = user;
    this.fullName = AccountService.getUserFullName(user);
    this.accountRoute = AccountService.accountRoute(user).join('/');
    this.avatar = {
      type: 'avatar',
      img: FilesService.getFileUrlBySize(user?.userpic_id, ImageSizeEnum.sm),
      title: this.fullName,
      link: this.accountRoute,
      form: 'circle',
    };
  }

  private loadAuthorDetails(id: string) {
    if (!id) {
      return;
    }
    this.accountService
      .getUserById(id, 'LOAD_TIME')
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (user) => {
          this.getAuthorDetails(user);
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public toggleCommentForm() {
    this.commentForm = !this.commentForm;
    this.detectChanges();
  }

  public openComplainForm() {
    this.complainForm = true;
    this.detectChanges();
  }

  public closeComplainForm() {
    this.complainForm = false;
    this.detectChanges();
  }

  public toggleLike() {
    const isLiked = this.comment.visit_info.is_liked;
    const action = isLiked ? this.commentService.unLike(this.comment.id) : this.commentService.like(this.comment.id);

    action.pipe(first(), takeUntil(this.destroyed$)).subscribe({
      next: () => {
        this.comment.visit_info.is_liked = !isLiked;
        this.comment.stats.likes_count = this.comment.stats.likes_count + (isLiked ? -1 : 1);
        this.detectChanges();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'SAVE-PROBLEM');
        this.comment.visit_info.is_liked = false;
        this.comment.stats.likes_count = this.comment.stats.likes_count;
        this.detectChanges();
      },
    });
  }

  public removeComment() {
    this.commentService
      .remove(this.comment.id)
      .pipe(first(), takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.removed.emit(true);
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'SAVE-PROBLEM');
          this.detectChanges();
        },
      });
  }

  public get likesCount(): number {
    return this.comment.stats.likes_count;
  }

  public get isLiked(): boolean {
    return this.comment.visit_info.is_liked;
  }

  public get responses(): Comment[] {
    return [];
  }

  public onCommented() {
    this.commented.emit(true);
  }

  public authorize() {
    this.authService.showAuthModal();
  }

  public trackByFn(index) {
    return index;
  }
}
